import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Button from 'components/Button2';
import TestSATReport from 'pages/TestSATReport';
import TestACTReport from 'pages/TestACTReport';
import TestSHSATReport from 'pages/TestSHSATReport';
import TestTSIA2Report from 'pages/TestTSIA2Report';
import TestBCAReport from 'pages/TestBCAReport';
import TestPaperACTReport from 'pages/TestPaperACTReport';

import './index.css';

// The report component for each test type the Student page can hand us. Keys
// match the `/report/<key>/:testId` route segments, so a selection made on the
// Students page reads the same either way. A type the Students page can
// select but that is missing here is dropped silently by parseSelection, so
// the two maps must be kept in step.
const REPORT_BY_TYPE = {
    'digital-sat': TestSATReport,
    'digital-act': TestACTReport,
    'digital-shsat': TestSHSATReport,
    'digital-tsia2': TestTSIA2Report,
    'digital-bca': TestBCAReport,
    'paper-act': TestPaperACTReport,
};

// Selections arrive in the query string as `tests=<id>:<type>,<id>:<type>`, so
// the print sheet survives a refresh and can be shared as a link. Router state
// would be lost on reload.
const parseSelection = (raw: string | null) => {
    if (!raw) return [];
    const seen = new Set<string>();
    return raw.split(',').reduce((kept, entry) => {
        const [testId, type] = entry.split(':');
        if (!testId || !REPORT_BY_TYPE[type]) return kept;
        // A duplicate id would mount the same report twice and print it twice.
        if (seen.has(testId)) return kept;
        seen.add(testId);
        kept.push({ testId, type });
        return kept;
    }, []);
};

function BulkPrintReports() {
    const [searchParams] = useSearchParams();
    const { studentId } = useParams();
    const navigate = useNavigate();

    const selection = useMemo(
        () => parseSelection(searchParams.get('tests')),
        [searchParams]
    );

    // Ids of the reports whose data has settled. A Set (not a counter) because
    // a report that remounts must not be able to count itself twice and let the
    // dialog open while another report is still empty.
    const [readyIds, setReadyIds] = useState(new Set<string>());
    // Guards the one automatic print. Re-printing afterwards is a button press.
    const printedRef = useRef(false);
    const containerRef = useRef(null);

    const allReady = selection.length > 0 && readyIds.size >= selection.length;

    // The route stays mounted if the selection changes underneath it (a new
    // ?tests= for the same path), so readiness and the one-shot print guard
    // have to be reset explicitly -- otherwise the new selection inherits a
    // spent guard and never opens its dialog.
    const selectionKey = searchParams.get('tests') ?? '';
    const selectionKeyRef = useRef(selectionKey);
    useEffect(() => {
        if (selectionKeyRef.current === selectionKey) return;
        selectionKeyRef.current = selectionKey;
        printedRef.current = false;
        setReadyIds(new Set<string>());
    }, [selectionKey]);

    // Stable per test id, so a report's fetch effect is not retriggered by this
    // page re-rendering as its siblings arrive.
    const markReady = useCallback((testId: string) => () => {
        setReadyIds(previous => {
            if (previous.has(testId)) return previous;
            const next = new Set(previous);
            next.add(testId);
            return next;
        });
    }, []);

    const print = useCallback(() => {
        // Isolation is scoped to this body class, exactly as the single-report
        // prints do, so no other page's print output is affected.
        document.body.classList.add('printing-bulk-reports');
        // Drop the browser's default page margins (and with them the auto
        // header/footer: date, title, URL, page number). Removed on afterprint
        // so other prints keep theirs.
        const pageStyle = document.createElement('style');
        pageStyle.textContent = '@page { margin: 0; }';
        document.head.appendChild(pageStyle);
        const cleanup = () => {
            document.body.classList.remove('printing-bulk-reports');
            pageStyle.remove();
            window.removeEventListener('afterprint', cleanup);
        };
        window.addEventListener('afterprint', cleanup);
        window.print();
    }, []);

    // Open the dialog once every report has its data AND the things that render
    // after data have caught up: KaTeX typesetting, webfonts and the
    // organization logos. Printing before those land produces raw LaTeX, wrong
    // metrics or missing logos on paper.
    useEffect(() => {
        if (!allReady || printedRef.current) return;
        printedRef.current = true;

        let cancelled = false;
        const imagesSettled = () => {
            const root = containerRef.current as HTMLElement | null;
            if (!root) return Promise.resolve();
            const pending = Array.from(root.querySelectorAll('img'))
                .filter((image: HTMLImageElement) => !image.complete)
                .map((image: HTMLImageElement) => new Promise(resolve => {
                    image.addEventListener('load', resolve, { once: true });
                    // An image that 404s must not hold the dialog shut.
                    image.addEventListener('error', resolve, { once: true });
                }));
            return Promise.all(pending);
        };

        const fontsSettled = (document as any).fonts?.ready ?? Promise.resolve();

        // A hard cap, so a wedged asset still yields a printable sheet rather
        // than a page that never prints and gives no reason why.
        const capped = Promise.race([
            Promise.all([imagesSettled(), fontsSettled]),
            new Promise(resolve => window.setTimeout(resolve, 8000)),
        ]);

        capped.then(() => {
            // One more frame for the KaTeX pass each report schedules on a
            // zero-delay timeout after its data lands.
            window.setTimeout(() => {
                if (!cancelled) print();
            }, 400);
        });

        return () => { cancelled = true; };
    }, [allReady, print]);

    if (selection.length === 0) {
        return (
            <div className='bulkPrint'>
                <Helmet>
                    <title>Print Reports - Test Center Admin</title>
                </Helmet>
                <div className='bulkPrint__bar'>
                    <p className='bulkPrint__status'>
                        No reports were selected to print.
                    </p>
                    <Button secondary onClick={() => navigate(-1)}>
                        BACK
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className='bulkPrint'>
            <Helmet>
                <title>Print Reports - Test Center Admin</title>
            </Helmet>
            {/* Screen-only control bar: never part of the printout. */}
            <div className='bulkPrint__bar'>
                <p className='bulkPrint__status'>
                    {allReady
                        ? `${selection.length} report${selection.length === 1 ? '' : 's'} ready.`
                        : `Loading ${readyIds.size} of ${selection.length} report${selection.length === 1 ? '' : 's'}...`
                    }
                </p>
                <div className='bulkPrint__buttons'>
                    <Button secondary onClick={() => navigate(`/students/${studentId}`)}>
                        BACK
                    </Button>
                    <Button disabled={!allReady} onClick={print}>
                        PRINT
                    </Button>
                </div>
            </div>
            {/*
                Keyed on the selection so the reports REMOUNT when ?tests=
                changes. Without this, an id present in both the old and new
                selection keeps the same `testIdOverride`, so its fetch effect
                never re-runs and it never fires onReady again -- while the
                reset effect above has just cleared readyIds. The result is a
                sheet stuck on "Loading N of M" that can never print.
            */}
            <div className='bulkPrint__reports' key={selectionKey} ref={containerRef}>
                {selection.map(({ testId, type }) => {
                    const Report = REPORT_BY_TYPE[type];
                    return (
                        // One page break per report, so each student's test
                        // starts on a fresh sheet.
                        <div className='bulkPrint__report' key={`${type}:${testId}`}>
                            <Report
                                testIdOverride={testId}
                                embedded
                                onReady={markReady(testId)}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default BulkPrintReports;
